import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { ZodNumber, ZodString } from "zod";
import {
  annualDateTimeframeSchema,
  fullDateTimeframeSchema,
} from "../marketing/segments/segment-timeframe-zod-schema";
import {
  ComparisonType,
  ExistsCompareOperators,
  ListCompareOperators,
  NumericCompareOperator,
  ProximityCompareOperators,
  ProximityUnit,
  StringCompareOperators,
  TokenCompareOperators,
  WhereConditionDataType,
} from "./segment-where-condition";
const listCompareSchema = (zType: ZodNumber | ZodString) =>
  z.object({
    type: z.literal(ComparisonType.LIST),
    operator: createEnum(ListCompareOperators, {
      enumName: "ListCompareOperators",
    }),
    values: z.array(zType),
  });

const tokenCompareSchema = z
  .object({
    type: z.literal(ComparisonType.TOKEN),
    operator: createEnum(TokenCompareOperators, {
      enumName: "TokenCompareOperators",
    }),
    values: z.array(z.string()),
  })
  .meta({ id: "tokenCompare" });

const tokenConditionSchema = <D extends z.ZodType<string>>(dimension: D) =>
  z.object({
    type: z.literal(WhereConditionDataType.TOKEN),
    dimension,
    comparison: tokenCompareSchema,
  });

const numericCompareSchema = z
  .object({
    type: z.literal(ComparisonType.NUMERIC),
    operator: createEnum(NumericCompareOperator, {
      enumName: "NumericCompareOperator",
    }),
    value: z.number(),
  })
  .meta({ id: "numericCompare" });

const numericConditionSchema = <D extends z.ZodType<string>>(dimension: D) =>
  z.object({
    type: z.literal(WhereConditionDataType.NUMERIC),
    dimension,
    comparison: numericCompareSchema,
  });

const booleanCompareSchema = z
  .object({ type: z.literal(ComparisonType.BOOLEAN), value: z.boolean() })
  .meta({ id: "booleanCompare" });

const existsCompareSchema = z
  .object({
    type: z.literal(ComparisonType.EXISTS),
    operator: createEnum(ExistsCompareOperators, {
      enumName: "ExistsCompareOperators",
    }),
  })
  .meta({ id: "existsCompare" });

const booleanConditionSchema = <D extends z.ZodType<string>>(dimension: D) =>
  z.object({
    type: z.literal(WhereConditionDataType.BOOLEAN),
    dimension,
    comparison: booleanCompareSchema,
  });

const fullDateConditionSchema = <D extends z.ZodType<string>>(dimension: D) =>
  z.object({
    type: z.literal(WhereConditionDataType.DATE_FULL),
    dimension,
    comparison: fullDateTimeframeSchema,
  });

const annualDateConditionSchema = <D extends z.ZodType<string>>(dimension: D) =>
  z.object({
    type: z.literal(WhereConditionDataType.DATE_ANNUAL),
    dimension,
    comparison: annualDateTimeframeSchema as z.ZodTypeAny,
  });

const tokenListConditionSchema = <D extends z.ZodType<string>>(dimension: D) =>
  z.object({
    type: z.literal(WhereConditionDataType.TOKEN_LIST),
    dimension,
    comparison: listCompareSchema(z.string()),
  });

const tokenHierarchyCompareSchema = z
  .object({
    type: z.literal(ComparisonType.TOKEN),
    operator: createEnum(TokenCompareOperators, {
      enumName: "TokenCompareOperators",
    }),
    prerequisiteValues: z.array(z.string()),
    values: z.array(z.string()),
  })
  .meta({ id: "tokenHierarchyCompare" });

const tokenHierarchyConditionSchema = <D extends z.ZodType<string>>(
  dimension: D,
) =>
  z.object({
    type: z.literal(WhereConditionDataType.TOKEN_HIERARCHY),
    dimension,
    comparison: tokenHierarchyCompareSchema,
  });

const cityProximityCompareSchema = z
  .object({
    type: z.literal(ComparisonType.PROXIMITY),
    operator: createEnum(ProximityCompareOperators, {
      enumName: "ProximityCompareOperators",
    }),
    prerequisiteValues: z.array(z.string()), // [country, state]
    value: z.string(), // city
    options: z.object({
      radius: z.number(),
      units: createEnum(ProximityUnit, { enumName: "ProximityUnit" }),
    }),
  })
  .meta({ id: "cityProximityCompare" });

const cityProximityConditionSchema = <D extends z.ZodType<string>>(
  dimension: D,
) =>
  z.object({
    type: z.literal(WhereConditionDataType.CITY_PROXIMITY),
    dimension,
    comparison: cityProximityCompareSchema,
  });

const stringCompareSchema = z
  .object({
    type: z.literal(ComparisonType.STRING),
    operator: createEnum(StringCompareOperators, {
      enumName: "StringCompareOperators",
    }),
    value: z.string(),
  })
  .meta({ id: "stringCompare" });

const stringConditionSchema = <D extends z.ZodType<string>>(dimension: D) =>
  z.object({
    type: z.literal(WhereConditionDataType.STRING),
    dimension,
    comparison: stringCompareSchema,
  });

const customFieldConditionSchema = <D extends z.ZodType<string>>(
  dimension: D,
) =>
  z.object({
    type: z.literal(WhereConditionDataType.CUSTOM_FIELD),
    dimension,
    declarationId: z.string(),
    comparison: z.union([
      tokenCompareSchema,
      numericCompareSchema,
      booleanCompareSchema,
      existsCompareSchema,
      fullDateTimeframeSchema,
    ]),
  });

/**
 * Every condition shape, over whatever vocabulary of dimensions the caller
 * has. Checkout segments and marketing segments share these shapes but not
 * their dimensions, so the dimension is a parameter rather than an open string.
 */
export function whereConditionSchemaFor<D extends z.ZodType<string>>(
  dimension: D,
) {
  return z.discriminatedUnion("type", [
    tokenConditionSchema(dimension),
    numericConditionSchema(dimension),
    booleanConditionSchema(dimension),
    fullDateConditionSchema(dimension),
    annualDateConditionSchema(dimension),
    tokenListConditionSchema(dimension),
    tokenHierarchyConditionSchema(dimension),
    cityProximityConditionSchema(dimension),
    customFieldConditionSchema(dimension),
    stringConditionSchema(dimension),
  ]);
}

export const whereConditionSchema = whereConditionSchemaFor(z.string()).meta({
  id: "whereCondition",
});

export type WhereCondition = z.infer<typeof whereConditionSchema>;

export type TokenCondition = Extract<
  WhereCondition,
  { type: WhereConditionDataType.TOKEN }
>;
export type NumericCondition = Extract<
  WhereCondition,
  { type: WhereConditionDataType.NUMERIC }
>;
export type BooleanCondition = Extract<
  WhereCondition,
  { type: WhereConditionDataType.BOOLEAN }
>;
export type FullDateCondition = Extract<
  WhereCondition,
  { type: WhereConditionDataType.DATE_FULL }
>;
export type AnnualDateCondition = Extract<
  WhereCondition,
  { type: WhereConditionDataType.DATE_ANNUAL }
>;
export type TokenListCondition = Extract<
  WhereCondition,
  { type: WhereConditionDataType.TOKEN_LIST }
>;
export type TokenHierarchyCondition = Extract<
  WhereCondition,
  { type: WhereConditionDataType.TOKEN_HIERARCHY }
>;
export type CityProximityCondition = Extract<
  WhereCondition,
  { type: WhereConditionDataType.CITY_PROXIMITY }
>;
export type CustomFieldCondition = Extract<
  WhereCondition,
  { type: WhereConditionDataType.CUSTOM_FIELD }
>;
export type StringCondition = Extract<
  WhereCondition,
  { type: WhereConditionDataType.STRING }
>;
